import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import API_BASE from '../services/apiBase';
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  MessageSquare,
  User,
  Building,
  List,
  ChevronDown,
  Headphones,
  Globe,
  Shield,
  ArrowRight,
} from 'lucide-react';
import heroBg from '../assets/auth/logistics-hero.png';
import '../styles/contact-page.css';

const PHONE_LINES = ['WhatsApp: +60 11-2119 0765', 'Phone: +60 11-6053 6205'];

const EMAIL_LINES = ['jetshippingandlogistic@gmail.com', 'support@jetshippingandlogistic.com'];

const ADDRESS_LINES = [
  'No.AZ-B1-07, Lot No.1B/2(A), Anzen Business Park',
  'Jalan 4/37A, Kawasan Industri Taman Bukit Maluri',
  'Kepong, Kuala Lumpur, 52100 Malaysia',
];

const HOURS_LINES = ['Monday – Friday: 8:00 AM – 6:00 PM', 'Saturday: 9:00 AM – 4:00 PM', 'Sunday: Closed'];

const FEATURES = [
  {
    Icon: Headphones,
    title: '24/7 support',
    text: 'Reach our team when timelines slip or lanes change.',
  },
  {
    Icon: Globe,
    title: 'Global reach',
    text: 'Air, sea, and land coverage across key trade corridors.',
  },
  {
    Icon: Shield,
    title: 'Secure delivery',
    text: 'Careful handling and compliance-aware coordination.',
  },
  {
    Icon: Clock,
    title: 'On-time delivery',
    text: 'Milestones and updates you can plan your operations around.',
  },
];

const SERVICE_OPTIONS = [
  'air-cargo',
  'sea-cargo',
  'fcl',
  'lcl',
  'land-transport',
  'customs-clearance',
  'warehousing',
  'other',
];

const Contact = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    const service = searchParams.get('service');
    if (service && SERVICE_OPTIONS.includes(service)) {
      setFormData((prev) => ({ ...prev, service }));
    }
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Your name is required';
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.service) errors.service = 'Please select a subject';
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          company: formData.company.trim(),
          service: formData.service,
          message: formData.message.trim(),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Could not send message');
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        message: '',
      });
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const msgLen = formData.message.length;

  return (
    <div className="contact-page">
      <section className="contact-hero" aria-labelledby="contact-hero-title">
        <div className="contact-hero__bg" style={{ backgroundImage: `url(${heroBg})` }} />
        <div className="contact-hero__overlay" />
        <div className="contact-hero__inner">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="contact-hero__crumb">
              <Link to="/">Home</Link>
              <span aria-hidden> &gt; </span>
              Contact us
            </p>
            <h1 id="contact-hero-title" className="contact-hero__title">
              We&apos;re here to help you
            </h1>
            <p className="contact-hero__lead">
              Have a question or need a quote? Our team is ready to assist you with the best cargo solutions.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="contact-main">
        <div className="contact-main__grid">
          <div>
            <motion.div
              className="contact-card"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <h2>Get in touch</h2>

              <div className="contact-block">
                <div className="contact-block__icon" aria-hidden>
                  <Phone className="w-5 h-5" strokeWidth={2} />
                </div>
                <div>
                  <h3>Phone</h3>
                  {PHONE_LINES.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
              </div>

              <div className="contact-block">
                <div className="contact-block__icon" aria-hidden>
                  <Mail className="w-5 h-5" strokeWidth={2} />
                </div>
                <div>
                  <h3>Email</h3>
                  {EMAIL_LINES.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
              </div>

              <div className="contact-block">
                <div className="contact-block__icon" aria-hidden>
                  <MapPin className="w-5 h-5" strokeWidth={2} />
                </div>
                <div>
                  <h3>Office address</h3>
                  {ADDRESS_LINES.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
              </div>

              <div className="contact-block">
                <div className="contact-block__icon" aria-hidden>
                  <Clock className="w-5 h-5" strokeWidth={2} />
                </div>
                <div>
                  <h3>Working hours</h3>
                  {HOURS_LINES.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              className="contact-quote"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08, duration: 0.4 }}
            >
              <p>Need a quick quote for your next shipment?</p>
              <Link to="/pricing" className="contact-quote__btn">
                Request a quote
                <ArrowRight className="w-4 h-4" aria-hidden />
              </Link>
              <span className="contact-quote__art" aria-hidden>
                📦
              </span>
            </motion.div>
          </div>

          <motion.div
            className="contact-card contact-form-card contact-panel"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4 }}
          >
            <h2>Send us a message</h2>

            {isSubmitted ? (
              <div style={{ textAlign: 'center', padding: '1.5rem 0.5rem' }}>
                <CheckCircle className="w-14 h-14" style={{ margin: '0 auto 1rem', color: '#16a34a' }} />
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem', color: '#166534' }}>
                  Message sent successfully!
                </h3>
                <p style={{ color: 'var(--c-muted)', marginBottom: '1.25rem', fontSize: '0.9rem' }}>
                  Thank you for contacting us. We&apos;ll get back to you within 24 hours.
                </p>
                <button type="button" onClick={() => setIsSubmitted(false)} className="btn">
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="contact-field">
                  <label htmlFor="contact-name">
                    Your name <span className="req">*</span>
                  </label>
                  <div className="contact-field__wrap">
                    <User className="w-4 h-4" aria-hidden />
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      className={`form-input ${fieldErrors.name ? 'error' : ''}`}
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your full name"
                      autoComplete="name"
                    />
                  </div>
                  {fieldErrors.name ? <span className="error-text">{fieldErrors.name}</span> : null}
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-email">
                    Email address <span className="req">*</span>
                  </label>
                  <div className="contact-field__wrap">
                    <Mail className="w-4 h-4" aria-hidden />
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      className={`form-input ${fieldErrors.email ? 'error' : ''}`}
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter your email"
                      autoComplete="email"
                    />
                  </div>
                  {fieldErrors.email ? <span className="error-text">{fieldErrors.email}</span> : null}
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-phone">Phone number</label>
                  <div className="contact-field__wrap">
                    <Phone className="w-4 h-4" aria-hidden />
                    <input
                      id="contact-phone"
                      type="tel"
                      name="phone"
                      className="form-input"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                      autoComplete="tel"
                    />
                  </div>
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-company">Company name</label>
                  <div className="contact-field__wrap">
                    <Building className="w-4 h-4" aria-hidden />
                    <input
                      id="contact-company"
                      type="text"
                      name="company"
                      className="form-input"
                      value={formData.company}
                      onChange={handleInputChange}
                      placeholder="Enter your company name"
                      autoComplete="organization"
                    />
                  </div>
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-subject">
                    Subject <span className="req">*</span>
                  </label>
                  <div className="contact-field__wrap">
                    <List className="w-4 h-4" aria-hidden />
                    <select
                      id="contact-subject"
                      name="service"
                      className={`form-input ${fieldErrors.service ? 'error' : ''}`}
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select a subject</option>
                      <option value="air-cargo">Air Cargo</option>
                      <option value="sea-cargo">Sea Cargo</option>
                      <option value="fcl">FCL (Full Container Load)</option>
                      <option value="lcl">LCL (Less than Container Load)</option>
                      <option value="land-transport">Land Transport</option>
                      <option value="customs-clearance">Customs Clearance</option>
                      <option value="warehousing">Warehousing</option>
                      <option value="other">Other</option>
                    </select>
                    <ChevronDown
                      className="w-4 h-4"
                      aria-hidden
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        left: 'auto',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        color: 'var(--c-muted)',
                      }}
                    />
                  </div>
                  {fieldErrors.service ? <span className="error-text">{fieldErrors.service}</span> : null}
                </div>

                <div className="contact-field">
                  <label htmlFor="contact-message">
                    Message <span className="req">*</span>
                  </label>
                  <div className="contact-field__wrap" style={{ alignItems: 'stretch' }}>
                    <MessageSquare
                      className="w-4 h-4"
                      aria-hidden
                      style={{ position: 'absolute', left: '0.75rem', top: '0.85rem' }}
                    />
                    <textarea
                      id="contact-message"
                      name="message"
                      className={`form-textarea ${fieldErrors.message ? 'error' : ''}`}
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      placeholder="Type your message here..."
                      rows={5}
                      maxLength={1000}
                      style={{ paddingLeft: '2.5rem', minHeight: '140px' }}
                    />
                  </div>
                  <div className="contact-field__count">
                    {msgLen} / 1000
                  </div>
                  {fieldErrors.message ? <span className="error-text">{fieldErrors.message}</span> : null}
                </div>

                {submitError ? (
                  <p className="error-text" role="alert" style={{ marginBottom: '0.75rem' }}>{submitError}</p>
                ) : null}

                <button
                  type="submit"
                  className="btn"
                  disabled={isSubmitting}
                  style={{ marginTop: '0.25rem' }}
                >
                  {isSubmitting ? (
                    <>
                      <span className="contact-btn-spinner" aria-hidden />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" aria-hidden />
                      Send message
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>

      <section className="contact-features" aria-label="Why contact us">
        <div className="contact-features__inner">
          {FEATURES.map(({ Icon, title, text }) => (
            <div key={title} className="contact-feat">
              <Icon className="w-5 h-5" strokeWidth={2} aria-hidden />
              <div>
                <strong>{title}</strong>
                <span>{text}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Contact;
